"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Draft", to: "Active, Cancelled" },
  { from: "Active", to: "Paused, Cancelled" },
  { from: "Paused", to: "Active, Cancelled" },
  {
    from: "Completed",
    to: "You cannot Mark As Completed yourself — the job does that when the last cycle is done. Cancelled may still be offered on the list",
  },
  {
    from: "Cancelled",
    to: "Nothing. Mark Recurring Bill As is hidden. Cancelled is a dead end",
  },
];

const RULES = [
  {
    topic: "This is a schedule, not a bill",
    rule: "Saving a Recurring Bill does not create a Bill, add stock, post the ledger, or change the vendor's Current Balance. Those happen later, when a real Bill is generated",
  },
  {
    topic: "Subscription gate",
    rule: "Creating a Recurring Bill is limited by your plan. Editing, Mark As, and deleting are not. Retrying a failed cycle (Create Bill on Recurring Activities) also checks Create and the plan feature",
  },
  {
    topic: "Save always makes Draft",
    rule: "There is no Save & Active. Nothing generates until you Mark As Active. Draft and Paused are skipped by the daily job",
  },
  {
    topic: "Recurring Bill number",
    rule: "Auto-generated unless you turn that off under Recurring Bill Configuration on the form (prefix, next number, Terms & Conditions). Default prefix is RECB. The field is labelled Bill#. Locked after save. Generated bills use Bill numbering (Bill Configuration), not RECB",
  },
  {
    topic: "Division, Branch, Warehouse, Vendor",
    rule: "All four are required to create, plus Profile Name, Repeat Every, Frequency, Start On, Payment Terms, shipping address, and at least one item. After save, location and vendor stay locked",
  },
  {
    topic: "No primary-bank gate",
    rule: "You can save a Recurring Bill without a primary bank",
  },
  {
    topic: "Stock and ledger live on the generated Bill",
    rule: "Settings → Inventory Config for Bill applies when the Bill is created. The Recurring Bill itself never takes warehouse quantity. Ledger and Current Balance follow the Bill rules once that Draft bill exists — including while it is still Draft",
  },
  {
    topic: "You can only edit or delete a Draft",
    rule: "Active, Paused, Completed, and Cancelled cannot be edited or deleted. Bills already generated from this schedule are not deleted with it",
  },
  {
    topic: "Paused does not catch up",
    rule: "While Paused, due dates are skipped. Marking Active again does not create the missed bills",
  },
  {
    topic: "Generated bills start as Draft",
    rule: "The job creates a Draft Bill, copies items, charges, notes, and payment terms, and sets due date from those terms on the generation date. You still Mark Bill As Open on the Bills module",
  },
  {
    topic: "Failed cycle retry",
    rule: "Recurring Activities shows FAILED with the error. Create Bill on that row makes a Draft bill from the current template and marks that cycle SUCCESS. Only a FAILED history row can be retried",
  },
  {
    topic: "No Duplicate, no Export, no Convert from",
    rule: "Nothing converts into a Recurring Bill. Outbound only: schedule → Bill",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a recurring bill" },
      { heading: "2. Repeat schedule" },
      { heading: "3. How bills actually get created" },
      { heading: "4. Viewing, searching, and the list toolbar" },
      { heading: "5. Editing a recurring bill" },
      { heading: "6. Changing status" },
      { heading: "7. Recurring Activities — success, failed, retry" },
      { heading: "8. The bill this schedule creates" },
      { heading: "9. Deleting a recurring bill" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function RecurringBillsPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Recurring Bills
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone who owes the same vendor, same items, on a fixed
            repeat — rent, retainers, standing purchase
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Recurring Bill</B> is a saved template plus a schedule. You
            fill it once, <B>Mark As Active</B>, and Books creates a real{" "}
            <B>Bill</B> on each due date. Those bills land as <B>Draft</B> in
            the Bills module — this page does not record a payment or apply
            credit. Prefix is <B>RECB</B>.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a recurring bill
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick Division, Branch, Warehouse. Only <B>Save</B> — that creates{" "}
            <B>Draft</B>. It does not start the schedule.
          </p>
          <Definitions
            items={[
              {
                term: "Profile Name",
                desc: <>Required. Name of the schedule, not the bill number.</>,
              },
              {
                term: "Bill# (Recurring Bill number)",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>Recurring Bill Configuration</B>. Default prefix{" "}
                    <B>RECB</B>. Locked after save.
                  </>
                ),
              },
              {
                term: "Vendor and shipping",
                desc: (
                  <>
                    Vendor required. Shipping Organization or Customer. After
                    save, vendor and location stay locked.
                  </>
                ),
              },
              {
                term: "Payment Terms",
                desc: (
                  <>
                    Required (the form label can read Payment Trems). Copied
                    onto each generated bill to calculate due date. There is
                    no due-date field on this form.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            No Bill is created on Save. No stock, no ledger, no Current
            Balance. There is no Duplicate and nothing converts into this
            module.
          </Callout>
          <Screenshot caption="Screenshot: Add Recurring Bills with Profile Name, Repeat Every, Start On, Never Expires, and Save only." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Repeat schedule
          </h3>
          <Definitions
            items={[
              {
                term: "Repeat Every / Frequency",
                desc: (
                  <>
                    Day(s), Week(s), Month(s), or Year(s), plus how many of
                    that period.
                  </>
                ),
              },
              {
                term: "When the first bill actually falls",
                desc: (
                  <>
                    For <B>Day(s)</B>, Start On itself is a generation day if
                    already Active. For Week / Month / Year, first generation
                    is Start On <B>plus</B> that frequency.
                  </>
                ),
              },
              {
                term: "Never Expires / End On",
                desc: (
                  <>
                    Never Expires keeps generating. With End On, after a
                    successful generation if the next due would fall on or
                    after End On, the profile becomes <B>Completed</B>.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            Schedule fields only stick while Draft. Pause does not reopen
            the form.
          </Callout>
          <Screenshot caption="Screenshot: Repeat Every Month(s), Frequency 1, Start On, End On, Never Expires." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. How bills actually get created
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            A daily job looks at every <B>Active</B> Recurring Bill. If today
            matches the pattern and this date has not already succeeded, it
            creates one Draft Bill.
          </p>
          <Screenshot caption="Screenshot: Next Bill on recurring bill detail." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Viewing, searching, and the list toolbar
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Status tabs Draft / Active / Paused / Completed / Cancelled. No
            Export. Preferences opens Recurring Bill Configuration. Detail:
            View Vendor, Download, Print. Tabs: Basic Info, Next Bill,
            Attachments, History, Recurring Activities.
          </p>
          <Screenshot caption="Screenshot: Recurring Bills list with Mark Recurring Bill As." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Editing a recurring bill
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Draft only. You can still change items, amounts, schedule, and
            address on a Draft. Vendor and location stay locked.
          </p>

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Changing status
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Button: <B>Mark Recurring Bill As</B>.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Mark As
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((t, i) => (
                  <tr
                    key={t.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[160px]">
                      {t.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Screenshot caption="Screenshot: Mark Recurring Bill As on a Draft — Active and Cancelled." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Recurring Activities — success, failed, retry
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Tab <B>Recurring Activities</B> → Execution Log &amp; Bills.
            SUCCESS / FAILED. FAILED row tooltip <B>Create Bill</B> retries
            from the current template and marks that cycle SUCCESS.
          </p>
          <Screenshot caption="Screenshot: Recurring Activities with a FAILED row and Create Bill." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. The bill this schedule creates
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            A normal Bill in the Bills module: Draft, Bill Configuration
            number, Inventory Config stock-add rules, Current Balance already
            updated. Mark it Open there before you pay it.
          </p>

          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Deleting a recurring bill
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Draft only. Generated bills stay in Bills.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/purchase/bills"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Bills <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where generated Draft bills land
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/recurring-expenses"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Recurring Expenses <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Same schedule shape, but generates posted expenses
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/recurring-invoices"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Recurring Invoices <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Sales-side sibling — generates Draft invoices
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
